import React from "react";
import ApprovedProofOrderSummary from "components/ApprovedProofOrderSummary";

type AddressLike = {
  first_name?: string;
  last_name?: string;
  address_details?: {
    addressLine1?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
  };
};

type ShippingMethodLike = {
  name?: string;
  description?: string;
};

export interface ApprovedProofCardProps {
  version: number;
  fileCount: number;
  attribution?: React.ReactNode;
  filesContent: React.ReactNode;
  files?: Array<{ id: string; name: string; quantity?: number }>;
  fileQuantities?: Array<{ project_file_id: string; quantity: number }>;
  onEdit?: () => void;
  showQtyPerFileDetails?: boolean;
  billingAddress?: AddressLike | null;
  shippingAddress?: AddressLike | null;
  poNumber?: string;
  quantity?: number;
  shippingMethod?: ShippingMethodLike | null;
  orderSummary?: string;
  onSaveOrderSummary?: (text: string) => Promise<void>;
  orderSummarySaving?: boolean;
}

function formatName(addr?: AddressLike | null): string {
  const first = String(addr?.first_name || "").trim();
  const last = String(addr?.last_name || "").trim();
  return [first, last].filter(Boolean).join(" ").trim() || "—";
}

function formatPhone(addr?: AddressLike | null): string {
  return String(addr?.address_details?.phone || "").trim() || "—";
}

function formatAddress(addr?: AddressLike | null): string {
  const d = addr?.address_details || {};
  const line1 = String((d as any).address_line_1 || d.addressLine1 || "").trim();
  const city = String(d.city || "").trim();
  const state = String(d.state || "").trim();
  const zip = String(d.zip || "").trim();
  return [line1, [city, state].filter(Boolean).join(", "), zip].filter(Boolean).join(" ").trim() || "—";
}

export default function ApprovedProofCard({
  version,
  fileCount,
  attribution,
  filesContent,
  files = [],
  fileQuantities = [],
  onEdit,
  showQtyPerFileDetails = true,
  billingAddress,
  shippingAddress,
  poNumber,
  quantity,
  shippingMethod,
  orderSummary,
  onSaveOrderSummary,
  orderSummarySaving = false,
}: ApprovedProofCardProps) {
  const qty = Number.isFinite(Number(quantity)) && Number(quantity) > 0 ? Math.floor(Number(quantity)) : 1;
  const poDisplay = String(poNumber || "").trim() || "-";

  const fileQtyMap = new Map<string, number>();
  fileQuantities.forEach((row) => {
    const id = String(row?.project_file_id || "").trim();
    if (!id) return;
    const q = Number(row?.quantity);
    fileQtyMap.set(id, Number.isFinite(q) && q >= 0 ? Math.floor(q) : 0);
  });
  const fileQtyRows = files.map((f) => {
    const id = String(f.id || "").trim();
    const fallback = Number.isFinite(Number(f.quantity)) ? Math.max(0, Math.floor(Number(f.quantity))) : 0;
    return {
      id: id || f.name,
      name: f.name || "File",
      qty: id && fileQtyMap.has(id) ? Number(fileQtyMap.get(id)) : fallback,
    };
  });

  return (
    <div className="pq-msg-proof-approved">
      <div className="pq-msg-proof-approved-head">
        <div className="pq-msg-proof-approved-head-left">
          <i className="ri-file-list-3-line pq-msg-proof-head-doc" aria-hidden />
          <span className="pq-msg-proof-version-pill">Proof V{version}</span>
          <span className="pq-msg-proof-file-count">
            {fileCount} file{fileCount !== 1 ? "s" : ""}
          </span>
          <span className="pq-msg-proof-approved-pill">Approved</span>
        </div>
        {attribution}
      </div>

      <div className="pq-msg-proof-rows">{filesContent}</div>

      <div
        style={{
          marginTop: 10,
          borderTop: "1px solid #d1fae5",
          borderBottom: "1px solid #d1fae5",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "10px 12px",
          background: "#fff",
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280", letterSpacing: 0.3 }}>
          ORDER DETAILS
        </div>
        {onEdit ? (
          <button
            type="button"
            onClick={onEdit}
            style={{
              border: "1px solid #cbd5e1",
              borderRadius: 8,
              background: "#fff",
              color: "#2563eb",
              padding: "4px 10px",
              fontSize: 13,
              fontWeight: 700,
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <i className="ri-edit-line" aria-hidden />
            Edit
          </button>
        ) : null}
      </div>

      <div
        style={{
          overflowX: "auto",
          overflowY: "hidden",
          WebkitOverflowScrolling: "touch",
        }}
      >
      <div
        style={{
          borderTop: "0",
          display: "grid",
          gridTemplateColumns: showQtyPerFileDetails
            ? "minmax(180px,1fr) minmax(180px,1fr) minmax(120px,max-content) minmax(220px,1.25fr) minmax(180px,1fr)"
            : "minmax(180px,1fr) minmax(180px,1fr) minmax(120px,max-content) minmax(180px,1fr)",
          minWidth: showQtyPerFileDetails ? 900 : 720,
          gap: 0,
          background: "#fff",
        }}
      >
        <div style={{ padding: "12px 14px", borderRight: "1px solid #d1fae5" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", marginBottom: 6 }}>BILLING</div>
          <div style={{ fontWeight: 700 }}>{formatName(billingAddress)}</div>
          <div>{formatPhone(billingAddress)}</div>
          <div>{formatAddress(billingAddress)}</div>
        </div>
        <div style={{ padding: "12px 14px", borderRight: "1px solid #d1fae5" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", marginBottom: 6 }}>SHIPPING</div>
          <div style={{ fontWeight: 700 }}>{formatName(shippingAddress)}</div>
          <div>{formatPhone(shippingAddress)}</div>
          <div>{formatAddress(shippingAddress)}</div>
        </div>
        <div style={{ padding: "12px 14px", borderRight: "1px solid #d1fae5" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", marginBottom: 6 }}>PO NUMBER</div>
          <div style={{ fontWeight: 700 }}>{poDisplay}</div>
        </div>
        {showQtyPerFileDetails ? (
          <div style={{ padding: "12px 14px", borderRight: "1px solid #d1fae5" }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", marginBottom: 6 }}>QTY PER FILE</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {(fileQtyRows.length > 0 ? fileQtyRows : [{ id: "total", name: "Total", qty }]).map((row) => (
                <div key={row.id} style={{ display: "grid", gridTemplateColumns: "30px 1fr", gap: 8, alignItems: "center" }}>
                  <span style={{ fontSize: 40, fontWeight: 800, lineHeight: 1 }}>{row.qty}</span>
                  <span
                    title={row.name}
                    style={{ fontSize: 13, color: "#4b5563", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  >
                    {row.name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        <div style={{ padding: "12px 14px" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#2563eb", marginBottom: 6 }}>SHIPPING</div>
          <div style={{ fontWeight: 700 }}>{String(shippingMethod?.name || "—").trim() || "—"}</div>
          <div style={{ color: "#6b7280" }}>{String(shippingMethod?.description || "").trim() || "—"}</div>
        </div>
      </div>
      </div>

      {onSaveOrderSummary ? (
        <ApprovedProofOrderSummary
          orderSummary={orderSummary}
          saving={orderSummarySaving}
          onSave={onSaveOrderSummary}
        />
      ) : null}
    </div>
  );
}

